import { useEffect, type ReactNode } from 'react'
import { Menu, PanelLeftOpen, Paperclip, SquarePen } from 'lucide-react'
import { navigeer, useKop, useRoute } from '../lib/router'
import { useStaat, zet, zetLade, zetZijbalkDicht } from '../lib/staat'
import Meldingen from './Meldingen'
import Zijbalk from './Zijbalk'

/* ===========================================================================
 *  Het geraamte: zoals elke chat-app, in de kleuren van het dashboard
 *
 *  Links de zijbalk met je gesprekken, rechts het werkvlak. Op een breed
 *  scherm is de zijbalk in te klappen (en dat onthoudt de browser); onder
 *  de 900 pixels is hij een lade die je met de hamburger opent, want op een
 *  telefoon is er voor een vaste zijbalk geen plek.
 *
 *  Boven het werkvlak een dunne balk met de naam van het gesprek -- en de
 *  knoppen om de zijbalk terug te halen als hij weg is.
 *
 *  Sneltoetsen: Ctrl+Shift+O nieuw gesprek, / naar het invoerveld (zie
 *  chat/Invoer.tsx), Esc doet de lade dicht.
 * =========================================================================== */

/** Een nieuw gesprek, en meteen in het invoerveld. */
export function nieuwGesprek() {
  zetLade(false)
  navigeer('/')
  /* Na het navigeren staat het veld er; een gebeurtenis in plaats van een
     verwijzing, want het veld hoort bij het gespreksscherm. */
  requestAnimationFrame(() => window.dispatchEvent(new Event('cowork:invoer')))
}

export default function Shell({ children }: { children: ReactNode }) {
  const dicht = useStaat((s) => s.zijbalkDicht)
  const lade = useStaat((s) => s.ladeOpen)
  const bestanden = useStaat((s) => s.bestandenTeller)
  const paneel = useStaat((s) => s.bestandenPaneel)
  const { pad } = useRoute()
  const kop = useKop()

  /* De lade gaat dicht zodra je ergens heen gaat. */
  useEffect(() => { zetLade(false) }, [pad])

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'o') {
        e.preventDefault()
        nieuwGesprek()
        return
      }
      if (e.key === 'Escape' && document.querySelector('.shell.lade-open')) zetLade(false)
    }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [])

  return (
    <div className={`shell ${dicht ? 'zijbalk-dicht' : ''} ${lade ? 'lade-open' : ''}`}>
      <Zijbalk />
      {lade && <div className="lade-sluier" onClick={() => zetLade(false)} aria-hidden />}

      <div className="hoofd">
        <header className="kopbalk">
          {/* Op een smal scherm de hamburger; op een breed alleen als de
              zijbalk is ingeklapt. CSS kiest welke er staat. */}
          <button type="button" className="kopbalk-knop alleen-smal" aria-label="Menu openen"
            aria-expanded={lade} onClick={() => zetLade(true)}>
            <Menu size={18} />
          </button>
          {dicht && (
            <button type="button" className="kopbalk-knop alleen-breed" aria-label="Zijbalk tonen"
              title="Zijbalk tonen" onClick={() => zetZijbalkDicht(false)}>
              <PanelLeftOpen size={18} />
            </button>
          )}
          <h1 className="kopbalk-titel" title={kop.titel || undefined}>{kop.titel}</h1>
          <span className="vul" />
          {bestanden !== null && (
            <button type="button" className={`kopbalk-knop met-teller ${paneel ? 'aan' : ''}`}
              aria-label={`Bestanden in dit gesprek (${bestanden})`} title="Bestanden in dit gesprek"
              aria-expanded={paneel} onClick={() => zet({ bestandenPaneel: !paneel })}>
              <Paperclip size={17} />
              {bestanden > 0 && <span className="kop-teller">{bestanden}</span>}
            </button>
          )}
          <button type="button" className={`kopbalk-knop ${dicht ? '' : 'alleen-smal'}`} aria-label="Nieuw gesprek"
            title="Nieuw gesprek (Ctrl+Shift+O)" onClick={nieuwGesprek}>
            <SquarePen size={17} />
          </button>
        </header>
        <main className="werk">{children}</main>
      </div>

      <Meldingen />
    </div>
  )
}
